import type { AppSettings, ModelInfo, ModelUpdate } from '../shared/types';
import { api, AuthError } from './api';
import { connectionOf, modelStatus, modelSummary, modelUpdateFromFields, publicModelUpdate, type Connection } from './models';

interface ManagerHooks {
  getSettings(): AppSettings | null;
  getModel(): string;
  onSaved(settings: AppSettings, preferredId?: string): Promise<void>;
}

let hooks: ManagerHooks;
let editingId = '';
let saving = false;
let integration: { oauthEnabled: boolean; url: string | null } | null = null;

function el(id: string): HTMLElement { return document.getElementById(id)!; }
function input(id: string): HTMLInputElement { return el(id) as HTMLInputElement; }
function select(id: string): HTMLSelectElement { return el(id) as HTMLSelectElement; }
function dialog(): HTMLDialogElement { return el('modelDialog') as HTMLDialogElement; }
function current(): ModelInfo | undefined { return hooks.getSettings()?.models.find((model) => model.id === editingId); }

function showError(message: string) {
  el('modelFormError').textContent = message;
  el('modelFormError').hidden = !message;
}

function showConnectionFields() {
  const connection = select('instanceConnection').value;
  el('instanceApiFields').hidden = connection !== 'api';
  el('instanceWebFields').hidden = connection !== 'web';
  el('instanceMcpFields').hidden = connection !== 'mcp';
  // Irrelevant fields must not block form submission with native validation.
  input('instanceBaseUrl').disabled = connection !== 'api';
  input('instanceApiModel').disabled = connection !== 'api';
  input('instanceApiKey').disabled = connection !== 'api';
  input('instanceWebUrl').disabled = connection !== 'web';
}

function renderList() {
  const list = el('modelInstanceList');
  list.replaceChildren();
  const models = hooks.getSettings()?.models ?? [];
  if (!models.length) {
    const note = document.createElement('p');
    note.className = 'model-list-empty';
    note.textContent = '每个实例保留独立的共读对话。先添加一个吧。';
    list.append(note);
  }
  for (const model of models) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'model-list-item';
    button.classList.toggle('selected', model.id === editingId);
    button.classList.toggle('archived', !!model.archived);
    button.setAttribute('aria-current', String(model.id === editingId));
    const name = document.createElement('strong');
    name.textContent = model.name;
    const meta = document.createElement('span');
    meta.textContent = modelSummary(model) || modelStatus(model);
    const state = document.createElement('small');
    state.textContent = model.archived ? '已归档 · 保留历史' : hooks.getSettings()?.defaultModel === model.id ? '默认实例' : modelStatus(model);
    button.append(name, meta, state);
    button.addEventListener('click', () => { if (!saving) edit(model); });
    list.append(button);
  }
}

function edit(model?: ModelInfo) {
  editingId = model?.id ?? crypto.randomUUID();
  (el('modelForm') as HTMLFormElement).reset();
  input('instanceName').value = model?.name ?? '';
  input('instanceModelLabel').value = model?.modelLabel ?? '';
  input('instanceChannel').value = model?.channel ?? '';
  select('instanceConnection').value = model ? connectionOf(model) : 'mcp';
  input('instanceBaseUrl').value = model?.providerConfig?.baseUrl ?? '';
  input('instanceApiModel').value = model?.providerConfig?.model ?? '';
  input('instanceApiKey').value = '';
  input('instanceApiKey').placeholder = model?.hasProvider ? '留空保留现有密钥' : '输入新密钥';
  input('instanceWebUrl').value = model?.webUrl ?? '';
  input('instanceMcpUrl').value = integration?.url ?? `${window.location.origin}/mcp`;
  input('instanceDefault').checked = hooks.getSettings()?.defaultModel === model?.id || !(hooks.getSettings()?.models.some((item) => !item.archived));
  input('instanceDefault').disabled = !!model?.archived;
  el('modelEditorTitle').textContent = model ? '编辑实例' : '添加实例';
  el('modelArchiveStatus').textContent = model?.archived ? '已归档' : '';
  el('instanceArchive').hidden = !model;
  el('instanceArchive').textContent = model?.archived ? '恢复实例' : '归档实例';
  el('instanceClearKeyWrap').hidden = !model?.hasProvider;
  el('instanceKeyHelp').textContent = model?.hasProvider
    ? '已保存密钥。留空会保留原值；输入内容会替换。密钥不回显，也不进入备份。'
    : '密钥只写入服务端，不会显示在这里或进入备份。';
  el('instanceIdDisplay').textContent = editingId;
  el('modelSaveStatus').textContent = '';
  showError('');
  showConnectionFields();
  renderList();
}

export function openModelManager(create = false) {
  if (!hooks.getSettings()) return;
  edit(create ? undefined : hooks.getSettings()!.models.find((model) => model.id === hooks.getModel()));
  if (!dialog().open) dialog().showModal();
  input('instanceName').focus();
  void api.getIntegrations().then(({ mcp }) => {
    integration = mcp;
    input('instanceMcpUrl').value = mcp.url ?? `${window.location.origin}/mcp`;
    const url = new URL(input('instanceMcpUrl').value);
    const localAddress = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    el('instanceMcpHelp').textContent = !mcp.oauthEnabled
      ? 'OAuth 尚未启用。部署方需设置 PUBLIC_URL 和 AUTH_TOKEN 后，才能在 ChatGPT 授权连接。'
      : localAddress
        ? 'OAuth 已启用。这是本机测试地址；ChatGPT 需要可公开访问的 HTTPS 地址，才能远程连接。'
        : url.protocol !== 'https:'
          ? 'OAuth 已启用。ChatGPT 需要可公开访问的 HTTPS 地址，请先更新部署地址。'
          : 'OAuth 已启用。可在 ChatGPT 添加此 MCP 地址，授权后使用；具体模型仍在 ChatGPT 中选择。';
  }).catch(() => {
    el('instanceMcpHelp').textContent = '暂时无法确认 OAuth 状态。请稍后重新打开此面板，或检查服务部署。';
  });
}

function setSaving(value: boolean) {
  saving = value;
  for (const id of ['instanceSave', 'instanceArchive', 'modelAdd', 'modelDialogClose']) {
    (el(id) as HTMLButtonElement).disabled = value;
  }
  el('instanceSave').textContent = value ? '保存中…' : '保存实例';
}

async function persist(update: ModelUpdate, makeDefault: boolean) {
  const settings = hooks.getSettings();
  if (!settings || saving) return;
  setSaving(true);
  showError('');
  try {
    const models = settings.models.map((model) => model.id === update.id ? update : publicModelUpdate(model));
    if (!models.some((model) => model.id === update.id)) models.push(update);
    const active = models.filter((model) => !model.archived);
    const defaultModel = makeDefault && !update.archived
      ? update.id
      : active.some((model) => model.id === settings.defaultModel)
        ? settings.defaultModel
        : active[0]?.id ?? '';
    const saved = await api.saveSettings({ models, defaultModel });
    // Clear the write-only credential before any further async UI work.
    input('instanceApiKey').value = '';
    await hooks.onSaved(saved, settings.models.some((model) => model.id === update.id) ? undefined : update.id);
    edit(saved.models.find((model) => model.id === update.id));
    el('modelSaveStatus').textContent = update.archived ? '已归档，历史仍保留' : '已保存';
  } catch (error) {
    if (error instanceof AuthError) {
      input('instanceApiKey').value = '';
      dialog().close();
      return;
    }
    showError(error instanceof Error ? error.message : '保存失败，请重试');
  } finally {
    setSaving(false);
  }
}

export function initModelManager(h: ManagerHooks) {
  hooks = h;
  el('modelManage').addEventListener('click', () => openModelManager());
  el('modelAddWelcome').addEventListener('click', () => openModelManager(true));
  el('modelAdd').addEventListener('click', () => edit());
  el('modelDialogClose').addEventListener('click', () => dialog().close());
  dialog().addEventListener('cancel', (event) => { if (saving) event.preventDefault(); });
  dialog().addEventListener('close', () => { input('instanceApiKey').value = ''; });
  select('instanceConnection').addEventListener('change', showConnectionFields);
  el('modelForm').addEventListener('submit', (event) => {
    event.preventDefault();
    if (saving) return;
    try {
      const update = modelUpdateFromFields({
        id: editingId,
        name: input('instanceName').value,
        modelLabel: input('instanceModelLabel').value,
        channel: input('instanceChannel').value,
        connection: select('instanceConnection').value as Connection,
        baseUrl: input('instanceBaseUrl').value,
        apiModel: input('instanceApiModel').value,
        apiKey: input('instanceApiKey').value,
        webUrl: input('instanceWebUrl').value,
        clearProvider: input('instanceClearKey').checked,
      }, current());
      void persist(update, input('instanceDefault').checked);
    } catch (error) {
      showError(error instanceof Error ? error.message : '请检查填写的配置');
    }
  });
  el('instanceArchive').addEventListener('click', () => {
    const model = current();
    if (model) void persist({ ...publicModelUpdate(model), archived: !model.archived }, false);
  });
  el('instanceCopyId').addEventListener('click', () => {
    void navigator.clipboard.writeText(editingId).then(() => {
      el('modelSaveStatus').textContent = '实例 ID 已复制';
    }).catch(() => {
      showError('无法访问剪贴板，请选择上方实例 ID 手动复制');
    });
  });
}
